/**
 * Google Analytics + Microsoft Clarity + Meta Pixel (cookie consent gated).
 * GA4 / Clarity / Meta only run after analytics consent.
 * Clarity loads on web with consentv2 (UK/EEA).
 * Meta Pixel fires PageView and CompleteRegistration (on sign_up).
 */

import { CookieService } from '../services/legalService';
import { isCapacitor } from './platform';

const GA_MEASUREMENT_ID = 'G-TD12HG49Y0';
const CLARITY_PROJECT_ID =
  import.meta.env.VITE_CLARITY_PROJECT_ID?.trim() || 'x2916d2a3s';
/** Meta Ads pixel — override with VITE_META_PIXEL_ID if needed. */
const META_PIXEL_ID =
  import.meta.env.VITE_META_PIXEL_ID?.trim() || '2308098676665499';
let transportReady = false;
let scriptRequested = false;
let clarityScriptRequested = false;
let metaPixelReady = false;
let metaScriptRequested = false;
/** Events before gtag stub exists (e.g. consent just accepted). */
const pendingEvents = [];
/** Meta events queued until fbq is ready. */
const pendingMetaEvents = [];

const GA_CONFIG = {
  anonymize_ip: true,
  allow_google_signals: false,
  allow_ad_personalization_signals: false,
};

function hasAnalyticsConsent() {
  try {
    return CookieService.hasConsent('analytics');
  } catch {
    return false;
  }
}

function flushPendingEvents() {
  if (!window.gtag || pendingEvents.length === 0) return;
  const batch = pendingEvents.splice(0, pendingEvents.length);
  for (const { eventName, eventParams } of batch) {
    window.gtag('event', eventName, eventParams);
  }
}

function flushPendingMetaEvents() {
  if (!window.fbq || pendingMetaEvents.length === 0) return;
  const batch = pendingMetaEvents.splice(0, pendingMetaEvents.length);
  for (const { eventName, eventParams } of batch) {
    if (eventParams && Object.keys(eventParams).length > 0) {
      window.fbq('track', eventName, eventParams);
    } else {
      window.fbq('track', eventName);
    }
  }
}

/** Create gtag stub + config immediately (GA4 processes dataLayer when gtag.js loads). */
function ensureAnalyticsTransport() {
  if (!hasAnalyticsConsent()) return false;
  if (transportReady && window.gtag) return true;

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    window.gtag('js', new Date());
  }

  // Config must run before custom events — waiting for script onload drops early events.
  window.gtag('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('config', GA_MEASUREMENT_ID, GA_CONFIG);

  transportReady = true;
  flushPendingEvents();

  if (!scriptRequested) {
    scriptRequested = true;
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.onerror = () => {
      console.warn('Analytics: Failed to load gtag.js (blocked or network error)');
    };
    document.head.appendChild(script);
  }

  return true;
}

function applyClarityConsent() {
  if (!window.clarity) return false;

  const granted = hasAnalyticsConsent();
  window.clarity('consentv2', {
    ad_Storage: 'denied',
    analytics_Storage: granted ? 'granted' : 'denied',
  });
  console.log(
    'Analytics: Microsoft Clarity consentv2',
    granted ? 'analytics granted' : 'analytics denied'
  );
  return granted;
}

/** Load Clarity tag on web; recording follows consentv2 (required for UK/EEA). */
function ensureClarityScript() {
  if (!CLARITY_PROJECT_ID || isCapacitor()) return false;

  if (window.clarity) {
    applyClarityConsent();
    return true;
  }

  if (clarityScriptRequested) return false;

  clarityScriptRequested = true;
  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = `https://www.clarity.ms/tag/${i}`;
    t.onerror = () => {
      console.warn('Analytics: Failed to load Clarity (blocked or network error)');
    };
    t.onload = () => {
      console.log('Analytics: Microsoft Clarity loaded');
      applyClarityConsent();
    };
    y = l.getElementsByTagName(r)[0];
    if (y?.parentNode) {
      y.parentNode.insertBefore(t, y);
    } else {
      (l.head || l.documentElement).appendChild(t);
    }
  })(window, document, 'clarity', 'script', CLARITY_PROJECT_ID);

  applyClarityConsent();
  console.log('Analytics: Microsoft Clarity starting', CLARITY_PROJECT_ID);
  return true;
}

/**
 * Load Meta Pixel on web after analytics consent.
 * Used for Facebook/Instagram Sales campaigns (Complete Registration).
 */
function ensureMetaPixel() {
  if (!META_PIXEL_ID || isCapacitor()) return false;
  if (!hasAnalyticsConsent()) return false;

  if (window.fbq && metaPixelReady) {
    flushPendingMetaEvents();
    return true;
  }

  if (!window.fbq) {
    /* eslint-disable prefer-rest-params */
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable prefer-rest-params */
  }

  if (!metaScriptRequested) {
    metaScriptRequested = true;
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
    metaPixelReady = true;
    flushPendingMetaEvents();
    console.log('Analytics: Meta Pixel ready', META_PIXEL_ID);
  }

  return true;
}

function trackMetaEvent(eventName, eventParams = {}) {
  if (typeof window === 'undefined' || isCapacitor()) return;
  if (!hasAnalyticsConsent()) return;

  if (ensureMetaPixel() && window.fbq) {
    if (eventParams && Object.keys(eventParams).length > 0) {
      window.fbq('track', eventName, eventParams);
    } else {
      window.fbq('track', eventName);
    }
    return;
  }

  pendingMetaEvents.push({ eventName, eventParams });
}

/**
 * Initialize Google Analytics / Clarity / Meta when the user has consented.
 */
export const initializeAnalytics = async () => {
  ensureClarityScript();

  if (!hasAnalyticsConsent()) {
    console.log('Analytics: User has not consented to analytics cookies');
    return;
  }

  ensureAnalyticsTransport();
  applyClarityConsent();
  ensureMetaPixel();
  console.log('Analytics: transport ready (events will send when gtag.js loads)');
};

/**
 * Disable/stop tracking when consent is withdrawn.
 */
export const disableAnalytics = () => {
  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }

  if (window.clarity) {
    applyClarityConsent();
  }

  transportReady = false;
  scriptRequested = false;
  metaPixelReady = false;
  metaScriptRequested = false;
  pendingEvents.length = 0;
  pendingMetaEvents.length = 0;
  console.log('Analytics: disabled (consent withdrawn)');
};

/**
 * Track a page view (only if analytics transport is ready and consented).
 */
export const trackPageView = (path) => {
  ensureClarityScript();
  if (!hasAnalyticsConsent()) return;
  if (!ensureAnalyticsTransport()) return;
  window.gtag('config', GA_MEASUREMENT_ID, {
    ...GA_CONFIG,
    page_path: path,
  });
  trackMetaEvent('PageView');
};

/**
 * Track an event (queues until consent + gtag stub; config runs before send).
 *
 * Document pack conversions:
 * - sign_up — method: email | google | apple (also fires Meta CompleteRegistration)
 * - pack_try_started — anonymous landing/try build started
 * - pack_preview_ready — free preview report finished on web
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window === 'undefined') return;

  if (!hasAnalyticsConsent()) {
    return;
  }

  if (ensureAnalyticsTransport()) {
    window.gtag('event', eventName, eventParams);
  } else {
    pendingEvents.push({ eventName, eventParams });
  }

  if (eventName === 'sign_up') {
    trackMetaEvent('CompleteRegistration', {
      content_name: eventParams?.method || 'signup',
      status: true,
    });
  }
};
